import React, { useState } from 'react';
import { format, startOfMonth, endOfMonth, startOfWeek, endOfWeek, eachDayOfInterval, isSameMonth, isToday } from 'date-fns';
import { fr } from 'date-fns/locale';
import { Appointment, CalendarWorkingSettings } from './types';
import { CALENDAR_SETTINGS } from '../../config/calendarSettings';
import { DEFAULT_WORKDAY_START_TIME } from '../../config/workingTimeDefaults';
import {
  APPOINTMENT_CARD_BASE_CLASS,
  APPOINTMENT_CARD_PADDING_COMPACT_CLASS,
  APPOINTMENT_CARD_PADDING_REGULAR_CLASS,
  APPOINTMENT_CARD_TITLE_CLASS,
  APPOINTMENT_CARD_TIME_CLASS,
  getAppointmentStatusCardClass,
} from './statusStyles';
import { Modal } from '../UI/Modal';

interface MonthViewProps {
  currentDate: Date;
  appointments: Appointment[];
  workingSettings?: CalendarWorkingSettings;
  denseMode?: boolean;
  onAppointmentClick: (appointment: Appointment) => void;
  onTimeSlotClick: (startTime: Date, duration: number) => void;
  isLoading?: boolean;
}

const MONTH_WEEK_BASE_HEIGHT_PX = 128; // Tailwind h-32
const MONTH_DAY_TOP_BAR_HEIGHT_PX = 26;
const MONTH_DAY_VERTICAL_PADDING_PX = 16; // p-2 => 8px top + 8px bottom
const MONTH_APPOINTMENT_CARD_DENSE_HEIGHT_PX = 22;
const MONTH_APPOINTMENT_CARD_REGULAR_HEIGHT_PX = 40;
const MONTH_APPOINTMENT_GAP_DENSE_PX = 2;
const MONTH_APPOINTMENT_GAP_REGULAR_PX = 4;

const parseTimeToHoursMinutes = (value: string): { hours: number; minutes: number } | null => {
  const parts = value.split(':');
  if (parts.length !== 2) {
    return null;
  }

  const hours = Number(parts[0]);
  const minutes = Number(parts[1]);

  if (
    !Number.isInteger(hours) ||
    !Number.isInteger(minutes) ||
    hours < 0 ||
    hours > 23 ||
    minutes < 0 ||
    minutes > 59
  ) {
    return null;
  }

  return { hours, minutes };
};

const MonthView: React.FC<MonthViewProps> = ({
  currentDate,
  appointments,
  workingSettings,
  denseMode = true,
  onAppointmentClick,
  onTimeSlotClick,
  isLoading = false
}) => {
  const [expandedDay, setExpandedDay] = useState<{ date: Date; appointments: Appointment[] } | null>(null);
  const fallbackStart = {
    hours: CALENDAR_SETTINGS.workStartHour,
    minutes: 0,
  };
  const startOfWorkday =
    parseTimeToHoursMinutes(workingSettings?.workdayStartTime || DEFAULT_WORKDAY_START_TIME) ||
    fallbackStart;

  // Calculer les jours du mois à afficher (incluant les jours des mois précédent/suivant)
  const monthStart = startOfMonth(currentDate);
  const monthEnd = endOfMonth(currentDate);
  const calendarStart = startOfWeek(monthStart, { weekStartsOn: 1 }); // Lundi
  const calendarEnd = endOfWeek(monthEnd, { weekStartsOn: 1 });
  
  const allDays = eachDayOfInterval({ start: calendarStart, end: calendarEnd });

  // Grouper les jours par semaines
  const weeks = [];
  for (let i = 0; i < allDays.length; i += 7) {
    weeks.push(allDays.slice(i, i + 7));
  }

  // Filtrer les rendez-vous pour un jour spécifique
  const getAppointmentsForDay = (day: Date) => {
    return appointments.filter(appointment => {
      const appointmentDate = new Date(appointment.startTime);
      return format(appointmentDate, 'yyyy-MM-dd') === format(day, 'yyyy-MM-dd');
    });
  };

  const getAppointmentLabel = (appointment: Appointment) => {
    if (appointment.title?.trim()) {
      return appointment.title;
    }

    const firstName = appointment.client?.firstName || '';
    const lastName = appointment.client?.lastName || '';
    const fullName = `${firstName} ${lastName}`.trim();

    return fullName || 'Rendez-vous';
  };

  const handleDayClick = (day: Date) => {
    const clickDateTime = new Date(day);
    clickDateTime.setHours(startOfWorkday.hours, startOfWorkday.minutes, 0, 0);
    onTimeSlotClick(clickDateTime, CALENDAR_SETTINGS.defaultAppointmentDurationMinutes);
  };

  const handleAppointmentButtonClick = (
    event: React.MouseEvent<HTMLButtonElement>,
    appointment: Appointment
  ) => {
    event.stopPropagation();
    onAppointmentClick(appointment);
  };

  const handleShowMoreClick = (
    event: React.MouseEvent<HTMLButtonElement>,
    day: Date,
    dayAppointments: Appointment[]
  ) => {
    event.stopPropagation();
    setExpandedDay({ date: day, appointments: dayAppointments });
  };

  const handleExpandedAppointmentClick = (appointment: Appointment) => {
    setExpandedDay(null);
    onAppointmentClick(appointment);
  };

  const handleCreateAppointmentFromExpandedDay = () => {
    if (!expandedDay) {
      return;
    }

    const clickDateTime = new Date(expandedDay.date);
    clickDateTime.setHours(startOfWorkday.hours, startOfWorkday.minutes, 0, 0);
    setExpandedDay(null);
    onTimeSlotClick(clickDateTime, CALENDAR_SETTINGS.defaultAppointmentDurationMinutes);
  };

  const renderAppointmentButton = (appointment: Appointment, dense = false) => {
    const label = getAppointmentLabel(appointment);
    const appointmentStartTime = format(new Date(appointment.startTime), 'HH:mm');
    const appointmentEndTime = format(new Date(appointment.endTime), 'HH:mm');
    const appointmentTimeRange = `${appointmentStartTime} - ${appointmentEndTime}`;
    const denseCardClass = dense ? 'px-1.5 py-0.5' : APPOINTMENT_CARD_PADDING_COMPACT_CLASS;
    const cardHeight = dense
      ? MONTH_APPOINTMENT_CARD_DENSE_HEIGHT_PX
      : MONTH_APPOINTMENT_CARD_REGULAR_HEIGHT_PX;

    return (
      <button
        key={appointment.id}
        type="button"
        className={`${APPOINTMENT_CARD_BASE_CLASS} ${denseCardClass} ${getAppointmentStatusCardClass(appointment.status)} flex flex-col justify-center`}
        style={{ height: `${cardHeight}px` }}
        onClick={(event) => handleAppointmentButtonClick(event, appointment)}
        title={`${label} - ${appointmentTimeRange}`}
        aria-label={`Voir le rendez-vous ${label} de ${appointmentStartTime} a ${appointmentEndTime}`}
      >
        {dense ? (
          <div className="truncate text-[10px] font-medium leading-tight">
            {appointmentTimeRange} · {label}
          </div>
        ) : (
          <>
            <div className={APPOINTMENT_CARD_TITLE_CLASS}>
              {label}
            </div>
            <div className={APPOINTMENT_CARD_TIME_CLASS}>
              {appointmentTimeRange}
            </div>
          </>
        )}
      </button>
    );
  };

  // Jours de la semaine
  const weekDays = ['Lun', 'Mar', 'Mer', 'Jeu', 'Ven', 'Sam', 'Dim'];
  const monthEndLabel = format(monthEnd, 'dd/MM/yyyy');

  return (
    <div className="relative h-full overflow-y-auto overflow-x-hidden">
      <div className="h-full flex flex-col">
        {/* En-tête avec les jours de la semaine */}
        <div className="grid grid-cols-7 border-b border-gray-200 bg-gray-50">
          {weekDays.map((day) => (
            <div key={day} className="p-3 text-center border-r border-gray-200 last:border-r-0">
              <div className="text-sm font-medium text-gray-700">{day}</div>
            </div>
          ))}
        </div>

        {/* Grille du calendrier */}
        <div className="flex-1">
          {weeks.map((week) => {
            const dayStates = week.map((day) => {
              const dayAppointments = getAppointmentsForDay(day)
                .slice()
                .sort((a, b) => new Date(a.startTime).getTime() - new Date(b.startTime).getTime());
              const hasOverflowAppointments = dayAppointments.length > 2;
              const visibleAppointments = hasOverflowAppointments
                ? dayAppointments.slice(0, 1)
                : dayAppointments.slice(0, 2);
              const hiddenAppointmentsCount = hasOverflowAppointments
                ? dayAppointments.length - visibleAppointments.length
                : 0;
              const hasDenseAppointments = denseMode && (visibleAppointments.length >= 2 || hiddenAppointmentsCount > 0);
              const appointmentItemHeight = hasDenseAppointments
                ? MONTH_APPOINTMENT_CARD_DENSE_HEIGHT_PX
                : MONTH_APPOINTMENT_CARD_REGULAR_HEIGHT_PX;
              const appointmentGap = hasDenseAppointments
                ? MONTH_APPOINTMENT_GAP_DENSE_PX
                : MONTH_APPOINTMENT_GAP_REGULAR_PX;
              const totalRenderedItems = visibleAppointments.length + (hiddenAppointmentsCount > 0 ? 1 : 0);
              const totalGapHeight = Math.max(0, totalRenderedItems - 1) * appointmentGap;
              const appointmentsHeight = visibleAppointments.length * appointmentItemHeight;
              const overflowBadgeHeight = hiddenAppointmentsCount > 0 ? appointmentItemHeight : 0;
              const minimumCellHeight =
                MONTH_DAY_TOP_BAR_HEIGHT_PX +
                MONTH_DAY_VERTICAL_PADDING_PX +
                appointmentsHeight +
                overflowBadgeHeight +
                totalGapHeight;

              return {
                day,
                dayAppointments,
                visibleAppointments,
                hiddenAppointmentsCount,
                hasDenseAppointments,
                minimumCellHeight,
              };
            });

            const weekRowHeight = Math.max(
              MONTH_WEEK_BASE_HEIGHT_PX,
              ...dayStates.map((state) => state.minimumCellHeight)
            );

            return (
            <div
              key={week[0].toISOString()}
              className="grid grid-cols-7 border-b border-gray-200"
              style={{ height: `${weekRowHeight}px` }}
            >
              {dayStates.map((state) => {
                const isCurrentMonth = isSameMonth(state.day, currentDate);
                const isDayToday = isToday(state.day);
                const isOutsideCurrentMonth = !isCurrentMonth;
                const dayLabel = format(state.day, 'EEEE d MMMM');
                const dayHoverClassName = isDayToday ? 'hover:bg-slate-100' : 'hover:bg-blue-50';

                return (
                  <div
                    key={state.day.toISOString()}
                    className={`relative border-r border-gray-200 last:border-r-0 p-2 transition-colors ${dayHoverClassName} ${
                      isOutsideCurrentMonth ? 'bg-gray-50 text-gray-400' : ''
                    } ${isDayToday ? 'bg-blue-50' : ''}`}
                  >
                    <button
                      type="button"
                      className="absolute inset-0"
                      onClick={() => handleDayClick(state.day)}
                      aria-label={`Créer un rendez-vous le ${dayLabel}`}
                    />

                    {/* Numéro du jour */}
                    <div className="relative z-10 flex items-center justify-between mb-1 pointer-events-none">
                      <span className={`inline-flex h-6 w-6 shrink-0 items-center justify-center rounded-full text-sm font-medium leading-none tabular-nums ${
                        isDayToday ? 'bg-blue-600 text-white' : 'text-gray-900'
                      }`}>
                        {format(state.day, 'd')}
                      </span>
                    </div>

                    {/* Rendez-vous du jour */}
                    <div
                      className={`relative z-10 pointer-events-none overflow-hidden ${state.hasDenseAppointments ? 'space-y-0.5' : 'space-y-1'}`}
                    >
                      {state.visibleAppointments.map((appointment) => (
                        <div key={appointment.id} className="pointer-events-auto">
                          {renderAppointmentButton(appointment, state.hasDenseAppointments)}
                        </div>
                      ))}
                      {state.hiddenAppointmentsCount > 0 && (
                        <button
                          type="button"
                          className={`pointer-events-auto ${APPOINTMENT_CARD_BASE_CLASS} ${state.hasDenseAppointments ? 'px-1.5 py-0.5' : APPOINTMENT_CARD_PADDING_COMPACT_CLASS} border-l-4 border-slate-400 bg-slate-100 text-left text-slate-700 overflow-hidden transition-colors hover:bg-slate-200 flex flex-col justify-center`}
                          style={{
                            height: `${state.hasDenseAppointments ? MONTH_APPOINTMENT_CARD_DENSE_HEIGHT_PX : MONTH_APPOINTMENT_CARD_REGULAR_HEIGHT_PX}px`
                          }}
                          onClick={(event) => handleShowMoreClick(event, state.day, state.dayAppointments)}
                          aria-label={`Afficher les ${state.hiddenAppointmentsCount} rendez-vous supplémentaires`}
                        >
                          {state.hasDenseAppointments ? (
                            <div className="truncate text-[10px] font-medium leading-tight">
                              +{state.hiddenAppointmentsCount} autre(s)
                            </div>
                          ) : (
                            <>
                              <div className={APPOINTMENT_CARD_TITLE_CLASS}>
                                +{state.hiddenAppointmentsCount} rendez-vous
                              </div>
                              <div className={APPOINTMENT_CARD_TIME_CLASS}>
                                Afficher la liste
                              </div>
                            </>
                          )}
                        </button>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )})}
        </div>

        <div className="mt-auto border-t border-slate-200 bg-slate-50 px-3 py-1 text-[11px] text-slate-500">
          Fin de periode: {monthEndLabel}
        </div>
      </div>

      {/* Message si aucun rendez-vous */}
      {appointments.length === 0 && !isLoading && (
        <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
          <div className="text-center text-gray-500">
            <div className="text-lg font-medium">Aucun rendez-vous ce mois</div>
            <div className="text-sm">Cliquez sur un jour pour créer un rendez-vous</div>
          </div>
        </div>
      )}

      <Modal
        isOpen={Boolean(expandedDay)}
        onClose={() => setExpandedDay(null)}
        title={expandedDay ? `Rendez-vous du ${format(expandedDay.date, 'EEEE d MMMM yyyy', { locale: fr })}` : undefined}
        size="md"
      >
        {expandedDay && (
          <div className="space-y-3">
            <div className="flex justify-end">
              <button
                type="button"
                onClick={handleCreateAppointmentFromExpandedDay}
                className="rounded-lg bg-blue-600 px-3 py-1.5 text-sm font-medium text-white transition-colors hover:bg-blue-700"
              >
                Nouveau rendez-vous ce jour
              </button>
            </div>
            {expandedDay.appointments
              .slice()
              .sort((a, b) => new Date(a.startTime).getTime() - new Date(b.startTime).getTime())
              .map((appointment) => {
                const appointmentStartTime = format(new Date(appointment.startTime), 'HH:mm');
                const appointmentEndTime = format(new Date(appointment.endTime), 'HH:mm');
                const appointmentTimeRange = `${appointmentStartTime} - ${appointmentEndTime}`;
                const label = getAppointmentLabel(appointment);

                return (
                  <button
                    key={appointment.id}
                    type="button"
                    onClick={() => handleExpandedAppointmentClick(appointment)}
                    className={`${APPOINTMENT_CARD_BASE_CLASS} ${APPOINTMENT_CARD_PADDING_REGULAR_CLASS} ${getAppointmentStatusCardClass(appointment.status)} transition-colors hover:brightness-95`}
                  >
                    <div className="min-w-0">
                      <div className={APPOINTMENT_CARD_TITLE_CLASS}>{label}</div>
                      <div className={APPOINTMENT_CARD_TIME_CLASS}>{appointmentTimeRange}</div>
                    </div>
                  </button>
                );
              })}
          </div>
        )}
      </Modal>
    </div>
  );
};

export default MonthView;